import * as React from 'react';

import { pickBy } from 'lodash';
import { compose, withProps } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import {
  capitalizeEachWord,
  getCustomCourseTerm,
  getCustomWeekTerm,
} from 'bundles/custom-labels/utils/customLabelsMapping';
import type { CustomLabelsV2 } from 'bundles/naptimejs/resources/__generated__/CustomLabelsV2';

import _t from 'i18n!nls/custom-labels';

import type { FormattingOptions, ReplaceCustomContent } from '../types/CustomLabels';
import { withIsWeekToModuleEnabledForCourse } from './withIsWeekToModuleEnabled';

type OuterProps = {
  customLabelsV2?: CustomLabelsV2;
};

type InnerProps = {
  replaceCustomContent: ReplaceCustomContent;
  isWeekToModuleEnabledForCourse: boolean;
};

export const useWithCustomLabelsMapping = ({
  customLabelsV2,
  isWeekToModuleEnabledForCourse,
}: OuterProps & { isWeekToModuleEnabledForCourse: boolean }): InnerProps => {
  const defaultCourseTerm = getCustomCourseTerm();
  const defaultWeekTerm = getCustomWeekTerm(isWeekToModuleEnabledForCourse);
  const defaultCapitalizedCourseTerm = capitalizeEachWord(defaultCourseTerm);
  const defaultCapitalizedWeekTerm = capitalizeEachWord(defaultWeekTerm);

  const getNumberedWeekTerms = (weekNumber: number) => {
    // TODO LRNENG-3369 Update these from week to module upon graduating the week to module project
    // In the meantime, custom-labels v2 will be adjusting the namings on the backend so no FE logic is required
    let weekWithNumber = isWeekToModuleEnabledForCourse
      ? _t('module #{weekNumber}', {
          weekNumber,
        })
      : _t('week #{weekNumber}', {
          weekNumber,
        });

    let capitalizedWeekWithNumber = isWeekToModuleEnabledForCourse
      ? _t('Module #{weekNumber}', {
          weekNumber,
        })
      : _t('Week #{weekNumber}', {
          weekNumber,
        });

    if (customLabelsV2) {
      const { namings, moduleNames, isRhyme } = customLabelsV2;
      const { courseraWeek } = namings;

      const courseraWeekWithTranslation = getCustomWeekTerm(isWeekToModuleEnabledForCourse, courseraWeek);
      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeekWithTranslation);

      if (isRhyme) {
        weekWithNumber = courseraWeek ?? weekWithNumber;
        capitalizedWeekWithNumber = courseraWeekWithCapitalization ?? capitalizedWeekWithNumber;
      } else if (moduleNames) {
        const { moduleName } = moduleNames.find(({ weekNumber: week }) => week === weekNumber) ?? {};

        if (moduleName) {
          weekWithNumber = moduleName ?? weekWithNumber;
          capitalizedWeekWithNumber = moduleName ?? capitalizedWeekWithNumber;
        }
      } else {
        weekWithNumber = _t('#{courseraWeek} #{weekNumber}', {
          courseraWeek: courseraWeek ?? defaultWeekTerm,
          weekNumber,
        });
        capitalizedWeekWithNumber = _t('#{courseraWeekWithCapitalization} #{weekNumber}', {
          courseraWeekWithCapitalization: courseraWeekWithCapitalization ?? defaultCapitalizedWeekTerm,
          weekNumber,
        });
      }
    }

    return {
      weekWithNumber,
      capitalizedWeekWithNumber,
    };
  };

  // Provide a replacement function as a prop to be used by consumers
  //
  // NOTE: these function signatures must match the ReplaceCustomContent signature in types/CustomLabels.ts
  // They use the 'function' syntax over the arrow syntax to support effective method overloading.
  // Without the overload the conditional return type specified by ReplaceCustomContent
  // will not work and TypeScript will not know how to cast the return values.
  //
  // For more info see:
  // - https://stackoverflow.com/questions/56109614/typescript-overload-arrow-function-is-not-working
  // - https://stackoverflow.com/questions/55059436/typescript-conditional-return-type-based-on-string-argument
  //
  function replaceCustomContent<T extends boolean>(
    message: string,
    options?: FormattingOptions<T>
  ): T extends true ? string : JSX.Element;
  function replaceCustomContent(message: string, options?: FormattingOptions<boolean>) {
    const { additionalVariables = {}, weekNumber, returnsString = false } = options ?? {};

    let labelOverrides = {};

    if (customLabelsV2) {
      const { courseraCourse, courseraWeek } = customLabelsV2.namings;

      const courseraCourseWithTranslation = getCustomCourseTerm(courseraCourse);
      const courseraWeekWithTranslation = getCustomWeekTerm(isWeekToModuleEnabledForCourse, courseraWeek);

      const courseraCourseWithCapitalization = capitalizeEachWord(courseraCourseWithTranslation);
      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeekWithTranslation);

      // There is no guarantee that the customLabelsV2 API will return labels for every
      // term, and since Object.assign() will brute copy over undefined values, we'll
      // use Lodash pickBy() to filter out falsy values. We could write our own helper method,
      // but seeing we have lodash at our disposal, we might as well use it.
      //
      // Read more:
      //   - https://github.com/lodash/lodash/issues/1093#issuecomment-87507761
      //   - https://stackoverflow.com/a/39977485/2466941
      //
      labelOverrides = pickBy({
        ...additionalVariables,
        course: courseraCourseWithTranslation,
        week: courseraWeekWithTranslation,
        capitalizedCourse: courseraCourseWithCapitalization,
        capitalizedWeek: courseraWeekWithCapitalization,
      });
    }

    const variables = {
      ...additionalVariables,
      course: defaultCourseTerm,
      week: defaultWeekTerm,
      capitalizedCourse: defaultCapitalizedCourseTerm,
      capitalizedWeek: defaultCapitalizedWeekTerm,
      ...labelOverrides,
      ...(weekNumber && getNumberedWeekTerms(weekNumber)),
    };

    if (returnsString) {
      return _t(message, variables);
    } else {
      return <FormattedMessage message={message} {...variables} />;
    }
  }

  return {
    replaceCustomContent,
    isWeekToModuleEnabledForCourse,
  };
};

export default <PropsFromCaller extends OuterProps>(Component: React.ComponentType<PropsFromCaller & InnerProps>) =>
  compose<PropsFromCaller & InnerProps, PropsFromCaller>(
    withIsWeekToModuleEnabledForCourse,
    withProps<InnerProps, PropsFromCaller & { isWeekToModuleEnabledForCourse: boolean }>(
      ({ customLabelsV2, isWeekToModuleEnabledForCourse }) => {
        return useWithCustomLabelsMapping({ customLabelsV2, isWeekToModuleEnabledForCourse });
      }
    )
  )(Component);
